/*
 * Copyright 2025 Mia srl
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useEffect, useRef } from 'react';
import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './Analytics.css';

const Analytics = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [timeframe, setTimeframe] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllMetrics, setShowAllMetrics] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [liveUpdateEnabled, setLiveUpdateEnabled] = useState(true);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
  }, [timeframe]);

  // Setup SSE for live updates
  useEffect(() => {
    if (!liveUpdateEnabled) return;

    const setupSSE = () => {
      try {
        const baseUrl = config.bffUrl || window.location.origin;
        const eventSource = new EventSource(`${baseUrl}/api/analytics/live`, {
          withCredentials: true
        });

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📡 Live analytics update:', data);
            
            if (data.type === 'info' && data.changeStreamSupported === false) {
              console.warn('⚠️ Live updates not available:', data.message);
              // Optionally notify user or disable live updates
              return;
            }
            
            if (data.type === 'event') {
              // Update dashboard data incrementally
              setDashboardData(prevData => {
                if (!prevData) return prevData;
                
                return {
                  ...prevData,
                  totalEvents: (prevData.totalEvents || 0) + 1,
                  eventTypeStats: updateEventTypeStats(prevData.eventTypeStats, data),
                };
              });
            }
          } catch (err) {
            console.error('Failed to parse SSE data:', err);
          }
        };

        eventSource.onerror = (err) => {
          console.error('SSE connection error:', err);
          eventSource.close();
          // Retry connection after 5 seconds
          setTimeout(setupSSE, 5000);
        };

        eventSourceRef.current = eventSource;
      } catch (err) {
        console.error('Failed to setup SSE:', err);
      }
    };

    setupSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [liveUpdateEnabled]);

  const updateEventTypeStats = (stats, newEvent) => {
    if (!stats || !newEvent.eventType) return stats;
    
    const updatedStats = [...stats];
    const existingIndex = updatedStats.findIndex(s => s.eventType === newEvent.eventType);
    
    if (existingIndex >= 0) {
      updatedStats[existingIndex] = {
        ...updatedStats[existingIndex],
        count: updatedStats[existingIndex].count + 1,
        lastOccurrence: new Date().toISOString(),
      };
    } else {
      updatedStats.push({
        _id: newEvent.eventType,
        eventType: newEvent.eventType,
        count: 1,
        uniqueUsers: 1,
        lastOccurrence: new Date().toISOString(),
      });
    }
    
    return updatedStats.sort((a, b) => b.count - a.count);
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = config.bffUrl || window.location.origin;
      const response = await getAuthorizedFetch()(`${baseUrl}/api/analytics/dashboard?timeframe=${timeframe}`, {
        headers: getBffHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }
      const data = await response.json();
      console.log('📊 Analytics dashboard data:', data);
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDashboardData();
    setIsRefreshing(false);
  };

  const toggleLiveUpdate = () => {
    setLiveUpdateEnabled(!liveUpdateEnabled);
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-error">Error: {error}</div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { 
    activeUsers, 
    eventTypeStats, 
    totalEvents, 
    totalSessions,
    languageStats = [],
    projectStats = [],
    topActions = [],
  } = dashboardData;

  const toStr = (val) => {
    if (val == null) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>Analytics Dashboard</h1>
        <div className="analytics-controls">
          <div className="timeframe-selector">
            <button
              className={timeframe === '24h' ? 'active' : ''}
              onClick={() => setTimeframe('24h')}
            >
              24h
            </button>
            <button
              className={timeframe === '7d' ? 'active' : ''}
              onClick={() => setTimeframe('7d')}
            >
              7d
            </button>
            <button
              className={timeframe === '30d' ? 'active' : ''}
              onClick={() => setTimeframe('30d')}
            >
              30d
            </button>
          </div>
          <div className="analytics-actions">
            <button
              className={`live-update-btn ${liveUpdateEnabled ? 'active' : ''}`}
              onClick={toggleLiveUpdate}
              title={liveUpdateEnabled ? 'Disable live updates' : 'Enable live updates'}
            >
              {liveUpdateEnabled ? '🔴 Live' : '⚫ Paused'}
            </button>
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? '↻ Refreshing...' : '↻ Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="analytics-grid">
        {/* Summary Cards */}
        <div className="analytics-card">
          <div className="card-title">Active Users</div>
          <div className="card-value">{activeUsers.count}</div>
          <div className="card-subtitle">{timeframe}</div>
        </div>

        <div className="analytics-card">
          <div className="card-title">Total Events</div>
          <div className="card-value">{totalEvents.toLocaleString()}</div>
          <div className="card-subtitle">All time</div>
        </div>

        <div className="analytics-card">
          <div className="card-title">Total Sessions</div>
          <div className="card-value">{totalSessions.toLocaleString()}</div>
          <div className="card-subtitle">All time</div>
        </div>

        <div className="analytics-card">
          <div className="card-title">Avg Events/Session</div>
          <div className="card-value">
            {totalSessions > 0 ? (totalEvents / totalSessions).toFixed(1) : '0'}
          </div>
          <div className="card-subtitle">All time</div>
        </div>

        {/* Event Types Table */}
        <div className="analytics-card wide">
          <div className="card-title">Event Types</div>
          <div className="event-types-table">
            <table>
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Count</th>
                  <th>Unique Users</th>
                  <th>Last Occurrence</th>
                </tr>
              </thead>
              <tbody>
                {eventTypeStats.slice(0, 10).map((stat) => (
                  <tr key={stat._id}>
                    <td>{stat.eventType || stat._id}</td>
                    <td>{stat.count.toLocaleString()}</td>
                    <td>{stat.uniqueUsers}</td>
                    <td>{new Date(stat.lastOccurrence).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Features */}
        <div className="analytics-card">
          <div className="card-title">Top Features</div>
          <div className="feature-list">
            {eventTypeStats
              .filter(stat => stat.eventType === 'feature_usage')
              .slice(0, 5)
              .map((stat, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-name">{stat.properties?.featureName || 'Unknown'}</span>
                  <span className="feature-count">{stat.count}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Activity Breakdown */}
        <div className="analytics-card">
          <div className="card-title">Activity Breakdown</div>
          <div className="activity-breakdown">
            {eventTypeStats.slice(0, 5).map((stat) => {
              const percentage = totalEvents > 0 ? (stat.count / totalEvents * 100).toFixed(1) : 0;
              return (
                <div key={stat._id} className="activity-item">
                  <div className="activity-label">
                    <span>{stat.eventType || stat._id}</span>
                    <span>{percentage}%</span>
                  </div>
                  <div className="activity-bar">
                    <div 
                      className="activity-bar-fill" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Language Statistics */}
        <div className="analytics-card wide">
          <div className="card-title">
            Top Languages Used
            {languageStats.length === 0 && <span className="empty-hint"> (No data yet - start coding!)</span>}
          </div>
          {languageStats.length > 0 ? (
            <div className="event-types-table">
              <table>
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Usage Count</th>
                    <th>Unique Users</th>
                    <th>Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {languageStats.slice(0, 10).map((stat) => (
                    <tr key={stat._id}>
                      <td><strong>{stat.language || 'Unknown'}</strong></td>
                      <td>{stat.count.toLocaleString()}</td>
                      <td>{stat.uniqueUsers}</td>
                      <td>{new Date(stat.lastUsed).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>💡 Language tracking will appear when you use trackLanguage() method</p>
              <code>trackLanguage('javascript', {'{'} action: 'edit' {'}'})</code>
            </div>
          )}
        </div>

        {/* Project Actions */}
        <div className="analytics-card">
          <div className="card-title">
            Project Actions
            {projectStats.length === 0 && <span className="empty-hint"> (No data yet)</span>}
          </div>
          {projectStats.length > 0 ? (
            <div className="feature-list">
              {projectStats.slice(0, 8).map((stat, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-name">
                    {toStr(stat.action)} ({stat.projectType || 'any'})
                  </span>
                  <span className="feature-count">{stat.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>💡 Track project actions with:</p>
              <code>trackProject('create', {'{'} projectType: 'nextjs' {'}'})</code>
            </div>
          )}
        </div>

        {/* Top Actions */}
        <div className="analytics-card">
          <div className="card-title">
            Top User Actions
            {topActions.length === 0 && <span className="empty-hint"> (No data yet)</span>}
          </div>
          {topActions.length > 0 ? (
            <div className="feature-list">
              {topActions.map((stat, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-name">
                    {toStr(stat.category)}: {toStr(stat.action)}
                  </span>
                  <span className="feature-count">{stat.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>💡 Track user actions with:</p>
              <code>trackAction('file', 'save', {'{'} target: 'App.jsx' {'}'})</code>
            </div>
          )}
        </div>

        {/* Visual Chart - Event Timeline */}
        <div className="analytics-card wide">
          <div className="card-title">Event Timeline (Last 7 Days)</div>
          <div className="chart-container">
            {eventTypeStats.length > 0 ? (
              <div className="bar-chart">
                {eventTypeStats.slice(0, 10).map((stat) => {
                  const maxCount = Math.max(...eventTypeStats.slice(0, 10).map(s => s.count));
                  const percentage = (stat.count / maxCount * 100).toFixed(1);
                  return (
                    <div key={stat._id} className="chart-bar-item">
                      <div className="chart-bar-label">
                        <span className="chart-bar-name">{stat.eventType || stat._id}</span>
                        <span className="chart-bar-value">{stat.count}</span>
                      </div>
                      <div className="chart-bar-container">
                        <div 
                          className="chart-bar-fill" 
                          style={{ width: `${percentage}%` }}
                          title={`${stat.count} events`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <p>Start using the app to see event timeline</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="analytics-card">
          <div className="card-title">Quick Insights</div>
          <div className="quick-stats">
            <div className="quick-stat-item">
              <span className="quick-stat-label">Most Active Event</span>
              <span className="quick-stat-value">
                {eventTypeStats.length > 0 ? eventTypeStats[0].eventType : 'N/A'}
              </span>
            </div>
            <div className="quick-stat-item">
              <span className="quick-stat-label">Unique Event Types</span>
              <span className="quick-stat-value">{eventTypeStats.length}</span>
            </div>
            <div className="quick-stat-item">
              <span className="quick-stat-label">Avg Session Duration</span>
              <span className="quick-stat-value">-</span>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="analytics-card info-card">
          <div className="card-title">📊 How to Get More Insights</div>
          <div className="info-content">
            <p>The analytics system is working! To see more detailed metrics:</p>
            <ul>
              <li><strong>Languages:</strong> Use <code>trackLanguage('javascript')</code></li>
              <li><strong>Projects:</strong> Use <code>trackProject('create')</code></li>
              <li><strong>Actions:</strong> Use <code>trackAction('file', 'save')</code></li>
              <li><strong>Code Gen:</strong> Use <code>trackCodeGeneration('python')</code></li>
            </ul>
            <p>Check the <a href="/learn" target="_blank">documentation</a> for more tracking methods.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
